/**
 * "Esqueceu o treino aberto?" (`D245`) — the words, the end times and the
 * three answers, presentational, so a story draws it bare. When it is asked
 * and what each answer does is `forgotten-ask.tsx`; which ends exist is
 * `forgotten-model.ts`.
 *
 * ── THREE ANSWERS ──────────────────────────────────────────────────────────
 * "Encerrar treino" ends it at the time chosen above — the last set by
 * default, because the minutes after it were not training. "Continuar
 * treinando" leaves it running. "Descartar treino" says what discarding
 * keeps: the sets stay, the workout does not count (`D177`). With nothing
 * logged there is no end to choose, and "Encerrar" is not offered.
 */
import { formatDurationMinutes } from "@repo/client/format";
import {
  "workout-live.forgotten_at_last" as atLast,
  "workout-live.forgotten_at_later" as atLater,
  "workout-live.forgotten_at_now" as atNow,
  "workout-live.forgotten_discard_hint" as discardHint,
  "workout-live.forgotten_discard" as discardLabel,
  "workout-live.forgotten_end" as endLabel,
  "workout-live.forgotten_lead" as forgottenLead,
  "workout-live.forgotten_lead_empty" as forgottenLeadEmpty,
  "workout-live.forgotten_title" as forgottenTitle,
  "workout-live.forgotten_when" as forgottenWhen,
  "workout-live.forgotten_keep" as keepLabel,
} from "@repo/i18n";
import { getLocale } from "@repo/i18n/runtime";
import { Check } from "lucide-react";
import { useCallback } from "react";
import { cx } from "../../../lib/cx";
import { appLocale } from "../../../lib/locale";
import { Button, Icon } from "../../../ui/controls";
import { Sheet } from "../../../ui/overlay";
import type {
  EndChoice,
  EndChoiceKind,
  ForgottenPrompt,
} from "./forgotten-model";

const CHOICE_LABEL: Record<EndChoiceKind, () => string> = {
  last: atLast,
  later: atLater,
  now: atNow,
};

/** "19:02" in the app's locale. */
export function timeOfDay(at: number): string {
  return new Intl.DateTimeFormat(getLocale(), {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(at));
}

export interface ForgottenSheetProps {
  /** The end chosen, one of `prompt.choices`. */
  chosen: EndChoiceKind;
  onChoose: (kind: EndChoiceKind) => void;
  onDiscard: () => void;
  onEnd: () => void;
  onKeep: () => void;
  onOpenChange: (open: boolean) => void;
  /** An answer is on its way: every button waits for it. */
  pending: boolean;
  prompt: ForgottenPrompt;
}

function ChoiceRow({
  choice,
  chosen,
  onChoose,
}: {
  choice: EndChoice;
  chosen: boolean;
  onChoose: (kind: EndChoiceKind) => void;
}) {
  const label = CHOICE_LABEL[choice.kind]();
  const { kind } = choice;
  const choose = useCallback(() => onChoose(kind), [onChoose, kind]);
  return (
    <label
      className={cx(
        "a-press flex min-h-12 w-full cursor-pointer items-center gap-3 rounded-2xl px-4 py-3 has-focus-visible:ring-2 has-focus-visible:ring-accent",
        chosen ? "bg-accent-soft text-accent-ink" : "bg-surface text-ink shadow"
      )}
      data-end-choice={kind}
    >
      <input
        checked={chosen}
        className="sr-only"
        name="forgotten-end"
        onChange={choose}
        type="radio"
        value={kind}
      />
      <span className="min-w-0 flex-1 font-semibold text-base">{label}</span>
      <span className="text-sm tabular-nums">{timeOfDay(choice.at)}</span>
      <span className="grid size-6 shrink-0 place-items-center">
        {chosen ? <Icon bold icon={Check} size={16} /> : null}
      </span>
    </label>
  );
}

export function ForgottenSheet(props: ForgottenSheetProps) {
  const { prompt } = props;
  const open = formatDurationMinutes(
    Math.floor(prompt.openMs / 60_000),
    appLocale()
  );
  const lead =
    prompt.lastSetAt === null
      ? forgottenLeadEmpty({ open })
      : forgottenLead({ open, time: timeOfDay(prompt.lastSetAt) });
  const canEnd = prompt.choices.length > 0;
  return (
    <Sheet
      ephemeral
      footer={
        <div className="flex flex-col gap-2">
          {canEnd ? (
            <Button
              className="w-full"
              disabled={props.pending}
              onClick={props.onEnd}
              size="lg"
            >
              {endLabel()}
            </Button>
          ) : null}
          <Button
            className="w-full"
            disabled={props.pending}
            onClick={props.onKeep}
            size="lg"
            variant={canEnd ? "secondary" : "primary"}
          >
            {keepLabel()}
          </Button>
          <Button
            className="w-full text-danger"
            disabled={props.pending}
            onClick={props.onDiscard}
            variant="ghost"
          >
            {discardLabel()}
          </Button>
          <p className="px-2 text-center text-ink-3 text-sm leading-snug">
            {discardHint()}
          </p>
        </div>
      }
      onOpenChange={props.onOpenChange}
      open
      title={forgottenTitle()}
    >
      <p className="text-base text-ink-2 leading-snug">{lead}</p>
      {canEnd ? (
        <fieldset className="mt-4">
          <legend className="mb-2 font-semibold text-ink-2 text-sm">
            {forgottenWhen()}
          </legend>
          <div className="flex flex-col gap-2">
            {prompt.choices.map((choice) => (
              <ChoiceRow
                choice={choice}
                chosen={choice.kind === props.chosen}
                key={choice.kind}
                onChoose={props.onChoose}
              />
            ))}
          </div>
        </fieldset>
      ) : null}
    </Sheet>
  );
}
